import {PinIcon} from '@primer/octicons-react'
import type {ReactElement} from 'react'
import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'
import type {ReaderFragment} from 'relay-runtime'
import {getFragmentIdentifier} from 'relay-runtime'

import {LABELS} from '../constants/labels'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {IssueCommentPinnedEvent$key} from './__generated__/IssueCommentPinnedEvent.graphql'
import type {IssueCommentUnpinnedEvent$key} from './__generated__/IssueCommentUnpinnedEvent.graphql'
import {IssueCommentCopyRendering, IssueCommentLink} from './IssueCommentPinShared'
import {RolledupIssueCommentPinEvent} from './RolledupIssueCommentPinEvent'
import {TimelineRow} from './row/TimelineRow'

type IssueCommentPinnedEventProps = {
  queryRef: IssueCommentPinnedEvent$key
  issueUrl: string
  onLinkClick?: (event: MouseEvent) => void
  highlightedEventId?: string
  refAttribute?: React.RefObject<HTMLLIElement | null>
  rollupGroup?: Record<string, Array<IssueCommentPinnedEvent$key | IssueCommentUnpinnedEvent$key>>
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const issueCommentPinnedEventFragment = graphql`
  fragment IssueCommentPinnedEvent on IssueCommentPinnedEvent {
    databaseId
    createdAt
    actor {
      ...TimelineRowEventActor
    }
    issueComment {
      url
      databaseId
    }
  }
`

const issueCommentPinnedEventFragmentNode = issueCommentPinnedEventFragment as unknown as ReaderFragment

const getPinnedFragmentIdentifier = (queryRef: IssueCommentPinnedEvent$key) =>
  getFragmentIdentifier(issueCommentPinnedEventFragmentNode, queryRef)

export function IssueCommentPinnedEvent({
  queryRef,
  issueUrl,
  onLinkClick,
  highlightedEventId,
  refAttribute,
  rollupGroup,
}: IssueCommentPinnedEventProps): React.ReactElement {
  const {actor, createdAt, databaseId} = useFragment(issueCommentPinnedEventFragment, queryRef)

  const highlighted = String(databaseId) === highlightedEventId

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      onLinkClick={onLinkClick}
      leadingIcon={PinIcon}
    >
      <TimelineRow.Main>
        <span data-testid="issue-comment-pinned-copy">
          {rollupGroup ? (
            <RolledupIssueCommentPinEvent rollupGroup={rollupGroup} />
          ) : (
            <PinnedCommentRendering queryRefs={[queryRef]} rollup={false} />
          )}
        </span>{' '}
      </TimelineRow.Main>
    </TimelineRow>
  )
}

type PinnedCommentRenderingProps = {
  queryRefs: IssueCommentPinnedEvent$key[]
  rollup: boolean
}

export const PinnedCommentRendering = ({queryRefs, rollup}: PinnedCommentRenderingProps): ReactElement | null => {
  return (
    <IssueCommentCopyRendering
      queryRefs={queryRefs}
      rollup={rollup}
      label={LABELS.timeline.issueCommentPinned.leading}
      getFragmentIdentifier={getPinnedFragmentIdentifier}
      renderLink={queryRef => <PinnedCommentLink queryRef={queryRef} />}
      linkPrefix="a "
    />
  )
}

export const PinnedCommentLink = ({queryRef}: {queryRef: IssueCommentPinnedEvent$key}): ReactElement => {
  const data = useFragment(issueCommentPinnedEventFragment, queryRef)
  return (
    <IssueCommentLink
      linkText={LABELS.timeline.issueCommentPinned.comment}
      url={data.issueComment?.url}
      testId="issue-comment-pinned-link"
    />
  )
}
